import {
  getProviderCatalogEntry,
  type GeneratedImageResult,
  type ImageGenParams,
  type ImageProvider,
  type ImageProviderConfig,
  type KeyVault,
  type ProviderProfile,
} from "@roqeet/core";
import { OpenAiImageProvider } from "./providers/openai-image.js";
import { GeminiImageProvider } from "./providers/gemini-image.js";
import { ImagenImageProvider } from "./providers/imagen.js";
import { ComfyImageProvider } from "./providers/comfy.js";
import { StableDiffusionWebUiProvider } from "./providers/stable-diffusion-webui.js";

type ImageProviderKind = "openai" | "gemini" | "imagen" | "comfy" | "stable-diffusion-webui";

/**
 * Image generation gateway. ProviderProfile is still the single persisted
 * connection object, while providerId/capabilities select the concrete image
 * adapter for multi-modal, image-only, and local backends.
 */
export class WebImageGateway {
  constructor(private readonly vault: KeyVault) {}

  private providerFor(profile: ProviderProfile): ImageProvider {
    switch (imageProviderFor(profile)) {
      case "gemini":
        return new GeminiImageProvider();
      case "imagen":
        return new ImagenImageProvider();
      case "comfy":
        return new ComfyImageProvider();
      case "stable-diffusion-webui":
        return new StableDiffusionWebUiProvider();
      case "openai":
      default:
        return new OpenAiImageProvider();
    }
  }

  async generate(
    params: ImageGenParams,
    profile: ProviderProfile,
  ): Promise<GeneratedImageResult[]> {
    const provider = this.providerFor(profile);
    const apiKey = profile.keyRef ? await this.vault.resolve(profile.keyRef) : undefined;

    const extraHeaders: Record<string, string> = { ...(profile.extraHeaders ?? {}) };
    if (apiKey) {
      if (imageProviderFor(profile) === "gemini" || imageProviderFor(profile) === "imagen") {
        extraHeaders["x-goog-api-key"] = apiKey;
      } else {
        extraHeaders["authorization"] = `Bearer ${apiKey}`;
      }
    }

    const config: ImageProviderConfig = {
      providerId: profile.providerId ?? profile.kind,
      extraHeaders,
      ...(profile.baseUrl ? { endpoint: profile.baseUrl } : {}),
      ...(profile.defaultModel ? { defaultModel: profile.defaultModel } : {}),
    };

    return provider.generate(params, config);
  }
}

function imageProviderFor(profile: ProviderProfile): ImageProviderKind {
  const ext = profile.extensions as { imageProvider?: string } | undefined;
  if (isImageProvider(ext?.imageProvider)) return ext.imageProvider;

  const catalogProvider = getProviderCatalogEntry(profile.providerId)?.imageProvider;
  if (catalogProvider) return catalogProvider;

  if (profile.kind === "google") return "gemini";
  return "openai";
}

function isImageProvider(value: string | undefined): value is ImageProviderKind {
  return (
    value === "openai" ||
    value === "gemini" ||
    value === "imagen" ||
    value === "comfy" ||
    value === "stable-diffusion-webui"
  );
}
